import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useSettings } from "@/context/SettingsContext";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { ArrowRight, Award, BookOpen, Calendar, Megaphone, Play, Trophy, Users } from "lucide-react";

function greet() {
  const h = new Date().getHours();
  if (h < 12) return "Good Morning";
  if (h < 17) return "Good Afternoon";
  return "Good Evening";
}

export default function Dashboard() {
  const { user } = useAuth();
  const { settings } = useSettings();
  const [enrollments, setEnrollments] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [live, setLive] = useState([]);
  const [leaders, setLeaders] = useState([]);
  const [certs, setCerts] = useState([]);
  const [courses, setCourses] = useState([]);

  const loadAll = useCallback(async () => {
    const [e, a, l, lb, cr, cs] = await Promise.all([
      api.get("/my/enrollments"), api.get("/announcements"),
      api.get("/live-sessions"), api.get("/leaderboard"),
      api.get("/my/certificates"), api.get("/courses"),
    ]);
    setEnrollments(e.data); setAnnouncements(a.data); setLive(l.data);
    setLeaders(lb.data.slice(0, 5)); setCerts(cr.data); setCourses(cs.data.slice(0, 6));
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  const totalProgress = enrollments.length
    ? Math.round(enrollments.reduce((a, e) => a + (e.progress || 0), 0) / enrollments.length)
    : 0;
  const continueLearning = enrollments.find((e) => (e.progress || 0) < 100) || enrollments[0];

  return (
    <div className="space-y-8" data-testid="dashboard">
      {/* Hero row */}
      <div className="grid lg:grid-cols-3 gap-5">
        <div className="lg:col-span-2 rounded-3xl royal-gradient text-white p-8 relative overflow-hidden grain">
          <div className="absolute -bottom-10 -right-10 h-60 w-60 rounded-full bg-gold/40 blur-3xl" />
          <p className="text-sm uppercase tracking-[0.25em] text-gold font-semibold">{greet()}</p>
          <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tight mt-2">
            {user?.name?.split(" ")[0] || "Student"} 👋
          </h1>
          <p className="mt-3 text-white/85 max-w-xl">
            You're {totalProgress}% into your career transformation journey. Keep going — every lesson compounds.
          </p>
          <div className="mt-6 flex items-center gap-3">
            <div className="flex-1 max-w-sm">
              <Progress value={totalProgress} className="h-2 bg-white/20" />
              <p className="text-xs text-white/70 mt-1.5">{totalProgress}% overall progress</p>
            </div>
            <Link to="/app/courses">
              <Button data-testid="explore-courses-btn" className="rounded-full bg-white text-primary hover:bg-white/90 btn-lift">
                Explore courses <ArrowRight className="ml-1 h-4 w-4" />
              </Button>
            </Link>
          </div>
        </div>

        {/* Points card */}
        <div className="rounded-3xl bg-card border border-border p-6 card-hover">
          <div className="flex items-center gap-3">
            <div className="h-11 w-11 rounded-2xl gold-gradient flex items-center justify-center">
              <Trophy className="h-5 w-5 text-[#1E3A8A]" />
            </div>
            <div>
              <p className="text-xs uppercase tracking-widest text-muted-foreground">Your points</p>
              <p className="font-display text-3xl font-bold">{user?.points || 0}</p>
            </div>
          </div>
          <div className="mt-5 space-y-2.5">
            {leaders.map((l, i) => (
              <div key={l.id} className="flex items-center justify-between text-sm">
                <span className="flex items-center gap-2">
                  <span className={`h-6 w-6 rounded-full text-xs font-bold flex items-center justify-center ${i === 0 ? "gold-gradient text-[#1E3A8A]" : "bg-secondary"}`}>
                    {i + 1}
                  </span>
                  <span className="truncate max-w-[120px]">{l.name}</span>
                </span>
                <span className="font-semibold text-gold">{l.points}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Continue learning + Recently watched */}
      <div className="grid lg:grid-cols-3 gap-5">
        {continueLearning && (
          <div className="lg:col-span-2 rounded-3xl bg-card border border-border overflow-hidden card-hover">
            <div className="grid sm:grid-cols-2">
              <img src={continueLearning.course.thumbnail} alt="" className="h-56 sm:h-full w-full object-cover" />
              <div className="p-6 flex flex-col justify-between">
                <div>
                  <p className="text-xs uppercase tracking-widest text-gold font-semibold">Continue learning</p>
                  <h3 className="font-display text-2xl font-bold mt-2">{continueLearning.course.title}</h3>
                  <p className="text-sm text-muted-foreground mt-2">{continueLearning.course.tagline}</p>
                  <Progress value={continueLearning.progress} className="mt-4 h-1.5" />
                  <p className="text-xs text-muted-foreground mt-1.5">{continueLearning.progress}% complete</p>
                </div>
                <Link to={`/app/courses/${continueLearning.course.slug}`}>
                  <Button data-testid="continue-learning-btn" className="mt-5 rounded-full royal-gradient border-0 text-white btn-lift">
                    <Play className="h-4 w-4 mr-1.5" /> Resume
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        )}

        <div className="rounded-3xl bg-card border border-border p-6 card-hover">
          <div className="flex items-center gap-2 mb-4">
            <Calendar className="h-4 w-4 text-primary" />
            <h3 className="font-display text-lg font-bold">Upcoming Live</h3>
          </div>
          <div className="space-y-3">
            {live.slice(0, 3).map((s) => (
              <div key={s.id} className="rounded-2xl border border-border p-3.5">
                <p className="font-semibold text-sm">{s.title}</p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {new Date(s.starts_at).toLocaleString([], { dateStyle: "medium", timeStyle: "short" })}
                </p>
                <a href={s.zoom_link} target="_blank" rel="noreferrer"
                   className="mt-2 inline-flex items-center text-xs font-semibold text-primary hover:underline">
                  Join session <ArrowRight className="ml-1 h-3 w-3" />
                </a>
              </div>
            ))}
            {live.length === 0 && <p className="text-sm text-muted-foreground">No upcoming classes.</p>}
          </div>
        </div>
      </div>

      {/* Announcements + Certificates + Recommended */}
      <div className="grid lg:grid-cols-3 gap-5">
        <div className="rounded-3xl bg-card border border-border p-6">
          <div className="flex items-center gap-2 mb-4">
            <Megaphone className="h-4 w-4 text-gold" />
            <h3 className="font-display text-lg font-bold">Announcements</h3>
          </div>
          <div className="space-y-3">
            {announcements.slice(0, 4).map((a) => (
              <div key={a.id} className="text-sm border-l-2 border-gold/60 pl-3">
                <p className="font-semibold">{a.title}</p>
                <p className="text-muted-foreground text-xs mt-0.5 line-clamp-2">{a.body}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="rounded-3xl bg-card border border-border p-6">
          <div className="flex items-center gap-2 mb-4">
            <Award className="h-4 w-4 text-gold" />
            <h3 className="font-display text-lg font-bold">Certificates</h3>
          </div>
          <p className="font-display text-4xl font-bold">{certs.length}</p>
          <p className="text-xs text-muted-foreground mt-1">certificates earned</p>
          <Link to="/app/certificates" className="mt-4 inline-flex items-center text-sm font-semibold text-primary hover:underline">
            View all <ArrowRight className="ml-1 h-3 w-3" />
          </Link>
        </div>

        <div className="rounded-3xl bg-card border border-border p-6">
          <div className="flex items-center gap-2 mb-4">
            <Users className="h-4 w-4 text-primary" />
            <h3 className="font-display text-lg font-bold">Community</h3>
          </div>
          <p className="text-sm text-muted-foreground mb-4">Join fellow learners on WhatsApp & Telegram.</p>
          <div className="flex gap-2">
            <a href={settings.whatsapp_community} target="_blank" rel="noreferrer" data-testid="dashboard-whatsapp"
               className="flex-1 rounded-xl bg-emerald-500 text-white text-sm font-semibold py-2 text-center btn-lift">WhatsApp</a>
            <a href={settings.telegram_link} target="_blank" rel="noreferrer"
               className="flex-1 rounded-xl bg-sky-500 text-white text-sm font-semibold py-2 text-center btn-lift">Telegram</a>
          </div>
        </div>
      </div>

      {/* Recommended courses */}
      <div>
        <div className="flex items-end justify-between mb-4">
          <div>
            <p className="text-xs uppercase tracking-[0.25em] text-gold font-semibold">Recommended</p>
            <h2 className="font-display text-3xl font-bold tracking-tight mt-1">Level up next</h2>
          </div>
          <Link to="/app/courses" className="text-sm font-semibold text-primary hover:underline">See all</Link>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {courses.map((c) => (
            <Link to={`/app/courses/${c.slug}`} key={c.id}
                  data-testid={`dashboard-course-${c.slug}`}
                  className="rounded-3xl bg-card border border-border overflow-hidden card-hover group">
              <div className="relative">
                <img src={c.thumbnail} alt={c.title} className="w-full h-40 object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                <span className="absolute top-3 left-3 rounded-full bg-gold/95 text-[#1E3A8A] text-[10px] font-bold px-2.5 py-1 uppercase tracking-widest">{c.category}</span>
              </div>
              <div className="p-4">
                <h3 className="font-display font-bold text-lg leading-snug">{c.title}</h3>
                <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{c.tagline}</p>
                <div className="mt-3 flex items-center justify-between text-xs text-muted-foreground">
                  <span className="flex items-center gap-1"><BookOpen className="h-3 w-3" />{c.modules?.reduce((s, m) => s + m.lessons.length, 0) || 0} lessons</span>
                  <span className="text-gold font-bold">{c.rating} ★</span>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}
